import './ProjectListRemoveModal.scss';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { Modal } from 'src/modules/common/components/Modal';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { PreserveContent } from 'src/modules/common/components/PreserveContent';
import { ProjectTitle } from 'src/modules/common/components/ProjectTitle';
import { BodyText } from 'src/modules/common/typography/BodyText';
import { LeadText } from 'src/modules/common/typography/LeadText';
import { ProjectListRemoving } from 'src/modules/project-list/types/ProjectListRemoving';

type Props = {
  readonly data: ProjectListRemoving | null;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
};

export const ProjectListRemoveModal = ({ data, onCancel, onSubmit }: Props): React.ReactElement => {
  const intl = useIntl();

  const handleClose = useCallback(() => {
    if (data?.status?.state !== 'pending') {
      onCancel();
    }
  }, [data?.status, onCancel]);

  return (
    <Modal
      className="bp-project-list-remove"
      opened={data !== null}
      handleClose={handleClose}
      size="sm"
    >
      <PreserveContent>
        {data && (
          <>
            <ModalHeader
              handleClose={handleClose}
              title={intl.formatMessage({ id: 'projectList/removeModal/title' })}
              danger={true}
            />

            <ModalBody danger={true}>
              <div className="bp-project-list-remove__body">
                <LeadText>
                  <FormattedMessage
                    id="projectList/removeModal/subtitle"
                  />
                </LeadText>

                <BodyText>
                  <FormattedMessage
                    id="projectList/removeModal/description"
                    values={{ projectName: <b><ProjectTitle title={data.project.data.title}/></b> }}
                  />
                </BodyText>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                kind="secondary"
                onClick={onCancel}
                autoFocus={true}
                fullWidth={true}
                disabled={data.status?.state === 'pending'}
              >
                <FormattedMessage id="projectList/removeModal/cancel"/>
              </Button>

              <Button
                disabled={data.status?.state === 'pending'}
                kind="danger"
                onClick={onSubmit}
                fullWidth={true}
                loading={data.status?.state === 'pending'}
              >
                <FormattedMessage id="projectList/removeModal/submit"/>
              </Button>
            </ModalFooter>
          </>
        )}
      </PreserveContent>
    </Modal>
  );
};
